import { useEffect, useState, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatError } from "@/lib/api";
import { tableLabel } from "@/lib/tables";
import AppHeader from "@/components/AppHeader";
import { Check, Clock, CheckCircle2, Sparkles, Plus, Minus, X } from "lucide-react";
import { toast } from "sonner";

function timeAgo(iso) {
    const now = new Date();
    const t = new Date(iso);
    const diff = Math.floor((now - t) / 1000);
    if (diff < 60) return `${diff} sn`;
    if (diff < 3600) return `${Math.floor(diff / 60)} dk`;
    return `${Math.floor(diff / 3600)} sa`;
}

const TABLE_COLORS = [
    "#D96C4A", "#6B8E7B", "#4A6FA5", "#B5651D",
    "#8B5CF6", "#0E7490", "#BE185D", "#65A30D",
];

function colorForTable(n) {
    return TABLE_COLORS[(n - 1) % TABLE_COLORS.length];
}

export default function KitchenDisplay() {
    const navigate = useNavigate();
    const [orders, setOrders] = useState([]);
    const [, setTick] = useState(0);
    const audioCtxRef = useRef(null);
    const knownIds = useRef(null); // null = first load, don't beep
    const newIds = useRef(new Set());

    const ensureAudio = useCallback(() => {
        if (!audioCtxRef.current) {
            const AC = window.AudioContext || window.webkitAudioContext;
            if (AC) {
                try {
                    audioCtxRef.current = new AC();
                } catch (e) {
                    console.warn("AudioContext init failed:", e?.message);
                }
            }
        }
        if (audioCtxRef.current && audioCtxRef.current.state === "suspended") {
            audioCtxRef.current.resume();
        }
        return audioCtxRef.current;
    }, []);

    const playAlert = useCallback(() => {
        const ctx = audioCtxRef.current;
        if (!ctx) return;
        // 3-beep alarm: high-pitch square wave bursts
        const freqs = [880, 1100, 880];
        freqs.forEach((freq, i) => {
            const osc = ctx.createOscillator();
            const gain = ctx.createGain();
            osc.type = "square";
            osc.frequency.value = freq;
            const start = ctx.currentTime + i * 0.35;
            gain.gain.setValueAtTime(0, start);
            gain.gain.linearRampToValueAtTime(0.5, start + 0.02);
            gain.gain.setValueAtTime(0.5, start + 0.22);
            gain.gain.linearRampToValueAtTime(0, start + 0.3);
            osc.connect(gain);
            gain.connect(ctx.destination);
            osc.start(start);
            osc.stop(start + 0.32);
        });
    }, []);

    const lateAlertedIds = useRef(new Set());
    const [expandedIds, setExpandedIds] = useState(new Set());
    const toggleExpanded = (id) => {
        setExpandedIds((prev) => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id);
            else next.add(id);
            return next;
        });
    };
    const [addTo, setAddTo] = useState(null);
    const [menu, setMenu] = useState([]);
    const [addCart, setAddCart] = useState({});
    const [addCategory, setAddCategory] = useState(null);
    const [adding, setAdding] = useState(false);

    const openAdd = async (order) => {
        setAddTo({ orderId: order.id, tableNumber: order.table_number });
        setAddCart({});
        if (menu.length === 0) {
            try {
                const { data } = await api.get("/menu");
                const avail = data.filter((x) => x.available);
                setMenu(avail);
                if (avail.length) setAddCategory(avail[0].category);
            } catch (e) {
                toast.error(formatError(e));
            }
        }
    };
    const closeAdd = () => { setAddTo(null); setAddCart({}); };
    const bumpAdd = (item, delta) => {
        setAddCart((prev) => {
            const cur = prev[item.id]?.qty || 0;
            const nxt = cur + delta;
            if (nxt <= 0) { const c = { ...prev }; delete c[item.id]; return c; }
            return { ...prev, [item.id]: { item, qty: Math.min(99, nxt) } };
        });
    };
    const submitAdd = async () => {
        const items = Object.values(addCart);
        if (!items.length || !addTo) return;
        setAdding(true);
        try {
            await api.post("/orders", {
                table_number: addTo.tableNumber,
                items: items.map((c) => ({ menu_item_id: c.item.id, qty: c.qty, notes: "" })),
            });
            toast.success(`Masa ${addTo.tableNumber} — ürün eklendi`);
            closeAdd();
            load();
        } catch (e) {
            toast.error(formatError(e));
        } finally {
            setAdding(false);
        }
    };
    const playLateAlarm = useCallback(() => {
        const ctx = audioCtxRef.current;
        if (!ctx) return;
        // 2-frequency siren — different from order beep (which is 3-freq high)
        const freqs = [500, 350];
        freqs.forEach((freq, i) => {
            const osc = ctx.createOscillator();
            const gain = ctx.createGain();
            osc.type = "sawtooth";
            osc.frequency.value = freq;
            const start = ctx.currentTime + i * 0.3;
            gain.gain.setValueAtTime(0, start);
            gain.gain.linearRampToValueAtTime(0.6, start + 0.02);
            gain.gain.setValueAtTime(0.6, start + 0.22);
            gain.gain.linearRampToValueAtTime(0, start + 0.28);
            osc.connect(gain);
            gain.connect(ctx.destination);
            osc.start(start);
            osc.stop(start + 0.3);
        });
    }, []);

    // Auto-unlock audio on the first user gesture anywhere on the page
    // (browser autoplay policy requires user interaction).
    useEffect(() => {
        const unlock = () => {
            ensureAudio();
            window.removeEventListener("pointerdown", unlock);
            window.removeEventListener("keydown", unlock);
        };
        window.addEventListener("pointerdown", unlock, { once: true });
        window.addEventListener("keydown", unlock, { once: true });
        // Also try immediately — works in some browsers when prior gesture
        // exists from the login click on the same tab.
        ensureAudio();
        return () => {
            window.removeEventListener("pointerdown", unlock);
            window.removeEventListener("keydown", unlock);
        };
    }, [ensureAudio]);

    const load = useCallback(async () => {
        try {
            const { data } = await api.get("/orders");
            const incomingIds = new Set(data.map((o) => o.id));
            // First load: just record IDs, don't trigger beep for existing orders
            if (knownIds.current === null) {
                knownIds.current = incomingIds;
            } else {
                let hasNew = false;
                for (const id of incomingIds) {
                    if (!knownIds.current.has(id)) {
                        newIds.current.add(id);
                        hasNew = true;
                        setTimeout(() => {
                            newIds.current.delete(id);
                            setTick((t) => t + 1);
                        }, 10000);
                    }
                }
                if (hasNew && audioCtxRef.current) {
                    playAlert();
                }
                knownIds.current = incomingIds;
            }
            setOrders(data.slice().sort((a, b) => new Date(b.created_at) - new Date(a.created_at)));
        } catch (e) {
            toast.error(formatError(e));
        }
    }, [playAlert]);

    useEffect(() => {
        load();
        const id = setInterval(load, 3000);
        const tid = setInterval(() => setTick((t) => t + 1), 1000);
        return () => {
            clearInterval(id);
            clearInterval(tid);
        };
    }, [load]);

    const toggleReady = async (orderId, itemId) => {
        try {
            await api.patch(`/orders/${orderId}/items/${itemId}/ready`);
            load();
        } catch (e) {
            toast.error(formatError(e));
        }
    };

    const pendingCount = orders.reduce(
        (s, o) => s + o.items.filter((i) => i.status !== "ready").length,
        0
    );

    return (
        <div className="min-h-screen bg-[#FAF9F6]">
            <AppHeader
                title="Mutfak Ekranı"
                right={
                    <div className="hidden md:flex items-center gap-3">
                        <button
                            onClick={() => navigate("/mutfak/masa")}
                            data-testid="kitchen-new-order"
                            className="inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold text-white bg-[#D96C4A] hover:bg-[#C05A3B]"
                        >
                            <Plus className="w-4 h-4" /> Yeni Sipariş
                        </button>
                        <div className="flex items-center gap-2 text-sm text-[#59605B] px-4 py-2 rounded-lg bg-[#F3F1EB]" data-testid="orders-count">
                            <Clock className="w-4 h-4" />
                            {orders.length} aktif sipariş
                        </div>
                        <div className="flex items-center gap-2 text-sm text-white px-4 py-2 rounded-lg bg-[#D96C4A]">
                            {pendingCount} bekleyen yemek
                        </div>
                    </div>
                }
            />
            <main className="max-w-[1800px] mx-auto px-6 md:px-8 py-6">
                <div className="mb-6">
                    <div className="text-xs font-semibold uppercase tracking-[0.2em] text-[#59605B] mb-2">Canlı Sipariş Ekranı</div>
                    <h1 className="font-display text-3xl sm:text-4xl font-semibold text-[#2C302E]">
                        Hazırlanacak siparişler
                    </h1>
                    <p className="text-sm text-[#59605B] mt-2">Ekran her 3 saniyede otomatik yenilenir. Yeni sipariş geldiğinde sesli uyarı çalar. Hazırlayıp masaya götürdüğünüz yemeklere dokunarak gönderildi işaretleyin.</p>
                </div>

                {orders.length === 0 ? (
                    <div className="bg-white border border-dashed border-[#E5E3D8] rounded-2xl p-16 text-center" data-testid="kitchen-empty">
                        <CheckCircle2 className="w-12 h-12 text-[#6B8E7B] mx-auto mb-4" />
                        <div className="font-display text-xl text-[#2C302E]">Bekleyen sipariş yok</div>
                        <div className="text-sm text-[#59605B] mt-2">Yeni sipariş geldiğinde burada görünecek.</div>
                    </div>
                ) : (
                    <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3">
                        {orders.map((order) => {
                            const allReady =
                                order.items.length > 0 &&
                                order.items.every((i) => i.status === "ready");
                            const minutesOld = Math.floor(
                                (new Date() - new Date(order.created_at)) / 60000
                            );
                            const isLate = minutesOld >= 10 && !allReady;
                            const isNew = newIds.current.has(order.id);
                            const collapsed = allReady && !isNew && !expandedIds.has(order.id);
                            if (isLate && !lateAlertedIds.current.has(order.id) && audioCtxRef.current) {
                                lateAlertedIds.current.add(order.id);
                                playLateAlarm();
                            }
                            const accent = colorForTable(order.table_number);

                            const headerBg = isLate
                                ? "#D96C4A"
                                : allReady
                                ? "#6B8E7B"
                                : accent;
                            const headerText = "#FFFFFF";

                            if (collapsed) {
                                return (
                                    <button
                                        key={order.id}
                                        onClick={() => toggleExpanded(order.id)}
                                        data-testid={`order-card-collapsed-${order.id}`}
                                        className="bg-white border border-[#6B8E7B]/40 rounded-2xl p-2 flex flex-col items-center justify-center hover:bg-[#6B8E7B]/5 fade-up min-h-[80px]"
                                    >
                                        <div className="text-[9px] font-semibold uppercase tracking-[0.15em] text-[#6B8E7B] leading-none">{tableLabel(order.table_number).split(" ").slice(0, -1).join(" ")}</div>
                                        <div
                                            className="font-display text-3xl font-bold leading-tight"
                                            style={{ color: colorForTable(order.table_number) }}
                                        >
                                            {tableLabel(order.table_number).split(" ").slice(-1)[0]}
                                        </div>
                                        <div className="text-[9px] font-semibold uppercase tracking-wider text-[#6B8E7B] mt-0.5">
                                            ✓ Tamamı gitti
                                        </div>
                                    </button>
                                );
                            }

                            return (
                                <div
                                    key={order.id}
                                    data-testid={`order-card-${order.id}`}
                                    className={`bg-white border rounded-2xl overflow-hidden flex flex-col fade-up transition-all ${
                                        isLate
                                            ? "border-[#D96C4A] late-blink"
                                            : isNew
                                            ? "border-[#D96C4A] ring-4 ring-[#D96C4A]/20 shadow-xl"
                                            : "border-[#E5E3D8] shadow-[2px_4px_12px_rgba(44,48,46,0.04)]"
                                    }`}
                                >
                                    <div
                                        className="px-3 py-2 flex justify-between items-center"
                                        style={{ backgroundColor: headerBg, color: headerText }}
                                    >
                                        <div>
                                            <div className="text-[9px] font-semibold uppercase tracking-[0.15em] opacity-80 leading-none">
                                                {tableLabel(order.table_number).split(" ").slice(0, -1).join(" ")}
                                            </div>
                                            <div className="font-display text-2xl font-bold leading-tight">
                                                {tableLabel(order.table_number).split(" ").slice(-1)[0]}
                                            </div>
                                            <div className="text-[10px] opacity-80 leading-tight">
                                                {order.waiter_username}
                                            </div>
                                        </div>
                                        <div className="flex flex-col items-end gap-0.5">
                                            {isNew && (
                                                <span
                                                    data-testid={`new-badge-${order.id}`}
                                                    className="inline-flex items-center gap-0.5 px-1.5 py-0 rounded-full bg-white/25 text-[9px] font-bold uppercase tracking-wider"
                                                >
                                                    <Sparkles className="w-2.5 h-2.5" />
                                                    Yeni
                                                </span>
                                            )}
                                            <div className="flex items-center gap-1 text-xs font-medium">
                                                <Clock className="w-3 h-3" />
                                                {timeAgo(order.created_at)}
                                            </div>
                                            {isLate && (
                                                <div className="text-[9px] font-bold uppercase tracking-wider">
                                                    Gecikmiş
                                                </div>
                                            )}
                                        </div>
                                    </div>

                                    <div className="flex-1 p-1.5 space-y-1">
                                        <button
                                            onClick={() => openAdd(order)}
                                            data-testid={`add-item-${order.id}`}
                                            className="w-full flex items-center justify-center gap-1.5 p-1.5 rounded-md border-2 border-dashed border-[#D96C4A]/40 text-[#D96C4A] hover:bg-[#D96C4A]/10 text-xs font-semibold uppercase tracking-wider"
                                        >
                                            <Plus className="w-3.5 h-3.5" /> Ürün Ekle
                                        </button>
                                        {order.items.map((item) => {
                                            const ready = item.status === "ready";
                                            return (
                                                <button
                                                    key={item.id}
                                                    onClick={() =>
                                                        toggleReady(order.id, item.id)
                                                    }
                                                    data-testid={`kds-item-${item.id}`}
                                                    className={`w-full text-left flex items-start gap-2 p-2 rounded-md border transition-all ${
                                                        ready
                                                            ? "bg-[#6B8E7B]/10 border-[#6B8E7B]/30"
                                                            : "bg-white border-[#E5E3D8] hover:bg-[#F3F1EB] hover:border-[#D96C4A]/40"
                                                    }`}
                                                >
                                                    <div
                                                        className={`w-5 h-5 rounded mt-0.5 flex items-center justify-center shrink-0 transition-all ${
                                                            ready
                                                                ? "bg-[#6B8E7B] text-white"
                                                                : "border-2 border-[#D9D6C8]"
                                                        }`}
                                                    >
                                                        {ready && <Check className="w-3 h-3" />}
                                                    </div>
                                                    <div className="flex-1 min-w-0">
                                                        <div
                                                            className={`font-medium leading-tight ${
                                                                ready
                                                                    ? "line-through text-[#59605B]"
                                                                    : "text-[#2C302E]"
                                                            }`}
                                                        >
                                                            <span
                                                                className="font-display text-base font-bold mr-0.5"
                                                                style={{
                                                                    color: ready
                                                                        ? "#59605B"
                                                                        : "#D96C4A",
                                                                }}
                                                            >
                                                                {item.qty}×
                                                            </span>
                                                            <span className="text-sm">{item.name}</span>
                                                        </div>
                                                        {item.notes && (
                                                            <div className="text-[11px] text-[#D96C4A] mt-0.5 font-medium leading-tight">
                                                                ⚠ {item.notes}
                                                            </div>
                                                        )}
                                                        <div className={`text-[10px] mt-0.5 font-semibold leading-tight ${
                                                            ready ? "text-[#6B8E7B]" : "text-[#59605B]"
                                                        }`}>
                                                            {ready
                                                                ? item.ready_at && (Date.now() - new Date(item.ready_at).getTime()) < 30000
                                                                    ? `✓ ${item.qty}/${item.qty} · ${Math.max(0, 30 - Math.floor((Date.now() - new Date(item.ready_at).getTime()) / 1000))}sn geri al`
                                                                    : `✓ ${item.qty}/${item.qty} gitti`
                                                                : (item.sent_qty || 0) > 0
                                                                    ? `${item.sent_qty}/${item.qty} gitti · dokunarak yolla`
                                                                    : `${item.qty} adet · dokunarak yolla`}
                                                        </div>
                                                        {item.added_by === "mutfak" && (
                                                            <div className="text-[9px] mt-0.5 font-bold uppercase tracking-wider text-[#D96C4A] text-right">
                                                                Mutfaktan eklendi
                                                            </div>
                                                        )}
                                                    </div>
                                                </button>
                                            );
                                        })}
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                )}
            </main>
            {addTo && (
                <div className="fixed inset-0 z-50 bg-[#2C302E]/50 backdrop-blur-sm flex items-end sm:items-center justify-center p-2 sm:p-4">
                    <div className="bg-white rounded-2xl w-full max-w-2xl max-h-[92vh] flex flex-col shadow-2xl fade-up">
                        <div className="px-5 py-3 border-b border-[#E5E3D8] flex items-center justify-between">
                            <div>
                                <div className="text-[10px] font-semibold uppercase tracking-[0.2em] text-[#59605B]">Ürün Ekle</div>
                                <div className="font-display text-xl font-semibold text-[#2C302E]">{tableLabel(addTo.tableNumber)}</div>
                            </div>
                            <button onClick={closeAdd} data-testid="close-add-modal" className="w-9 h-9 flex items-center justify-center rounded-lg hover:bg-[#F3F1EB] text-[#59605B]">
                                <X className="w-4 h-4" />
                            </button>
                        </div>
                        <div className="flex gap-2 overflow-x-auto px-4 py-3 border-b border-[#E5E3D8]">
                            {Array.from(new Set(menu.map((m) => m.category))).map((c) => (
                                <button key={c} onClick={() => setAddCategory(c)} data-testid={`add-cat-${c}`} className={`px-3 py-1.5 rounded-full text-xs font-medium whitespace-nowrap ${addCategory === c ? "bg-[#2C302E] text-white" : "bg-[#F3F1EB] text-[#2C302E]"}`}>{c}</button>
                            ))}
                        </div>
                        <div className="flex-1 overflow-y-auto p-3 grid grid-cols-2 sm:grid-cols-3 gap-2">
                            {menu.filter((m) => m.category === addCategory).map((item) => {
                                const inCart = addCart[item.id];
                                return (
                                    <div key={item.id} data-testid={`add-menu-${item.id}`} className={`bg-white border rounded-lg p-2.5 ${inCart ? "border-[#D96C4A]" : "border-[#E5E3D8]"}`}>
                                        <div className="text-sm font-semibold text-[#2C302E] leading-tight mb-1 truncate">{item.name}</div>
                                        <div className="text-xs text-[#D96C4A] font-semibold mb-2">₺{item.price.toFixed(2)}</div>
                                        {inCart ? (
                                            <div className="flex items-center gap-1 bg-[#F3F1EB] rounded p-1">
                                                <button onClick={() => bumpAdd(item, -1)} data-testid={`add-dec-${item.id}`} className="w-7 h-7 flex items-center justify-center rounded bg-white"><Minus className="w-3.5 h-3.5" /></button>
                                                <span className="flex-1 text-center text-sm font-bold" data-testid={`add-qty-${item.id}`}>{inCart.qty}</span>
                                                <button onClick={() => bumpAdd(item, 1)} data-testid={`add-inc-${item.id}`} className="w-7 h-7 flex items-center justify-center rounded bg-[#D96C4A] text-white"><Plus className="w-3.5 h-3.5" /></button>
                                            </div>
                                        ) : (
                                            <button onClick={() => bumpAdd(item, 1)} data-testid={`add-add-${item.id}`} className="w-full inline-flex items-center justify-center gap-1 px-2 py-1.5 rounded bg-[#2C302E] text-white text-xs font-medium"><Plus className="w-3.5 h-3.5" /> Ekle</button>
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                        <div className="px-5 py-3 border-t border-[#E5E3D8] flex items-center gap-3">
                            <div className="flex-1 text-sm text-[#59605B]">{Object.values(addCart).reduce((s, c) => s + c.qty, 0)} ürün seçildi</div>
                            <button onClick={submitAdd} disabled={adding || Object.keys(addCart).length === 0} data-testid="submit-add" className="px-5 py-2.5 rounded-lg bg-[#D96C4A] text-white font-semibold disabled:opacity-50">
                                {adding ? "Ekleniyor..." : "Siparişe Ekle"}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
